import React from 'react';
import ReactPlayer from 'react-player';
import { Document, Page } from './LazyPDF';
import { useState, useEffect, useRef, useCallback } from 'react';
import { AiOutlineZoomIn, AiOutlineZoomOut, AiOutlineDoubleLeft, AiOutlineDoubleRight } from "react-icons/ai";

export function PDFViewer({ url }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);

  // Track whether we're at the top or bottom of the scroll container
  // so we know when to flip to the previous or next page.
  const atTopRef = useRef(true);
  const atBottomRef = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Whenever the page changes, scroll the container back to the top (for
  // next-page) or to the bottom (for prev-page).  We store the scroll
  // direction in a ref so the effect can read it without triggering itself.
  const pendingScrollRef = useRef(null); // 'top' | 'bottom' | null

  const goToPrevPage = useCallback(() => {
    if (pageNumber > 1) {
      pendingScrollRef.current = 'bottom';
      setPageNumber((p) => p - 1);
    }
  }, [pageNumber]);

  const goToNextPage = useCallback(() => {
    if (pageNumber < numPages) {
      pendingScrollRef.current = 'top';
      setPageNumber((p) => p + 1);
    }
  }, [pageNumber, numPages]);

  // After the page state flips, execute the pending scroll.
  useEffect(() => {
    if (pendingScrollRef.current && containerRef.current) {
      if (pendingScrollRef.current === 'top') {
        containerRef.current.scrollTop = 0;
      } else {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
      pendingScrollRef.current = null;
    }
  }, [pageNumber]);

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  // Keep atTopRef / atBottomRef in sync as the user scrolls normally
  // inside a page.
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const scrollTop = Math.round(el.scrollTop);
    const scrollMax = el.scrollHeight - el.clientHeight;
    atTopRef.current = scrollTop <= 0;
    atBottomRef.current = scrollTop >= scrollMax - 1;
  }, []);

  // Wheel handler: let the container scroll naturally; only flip the page
  // when we are already at the respective edge.
  const handleWheel = useCallback(
    (e) => {
      const el = containerRef.current;
      if (!el) return;

      const scrollTop = Math.round(el.scrollTop);
      const scrollMax = el.scrollHeight - el.clientHeight;
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop >= scrollMax - 1;

      if (e.deltaY > 0 && atBottom && pageNumber < numPages) {
        // Scrolling down at the very bottom → next page
        e.preventDefault();
        goToNextPage();
      } else if (e.deltaY < 0 && atTop && pageNumber > 1) {
        // Scrolling up at the very top → prev page
        e.preventDefault();
        goToPrevPage();
      }
      // Otherwise let the browser handle it (normal in-page scroll)
    },
    [pageNumber, numPages, goToNextPage, goToPrevPage]
  );

  // Attach the wheel listener with { passive: false } so we can call
  // preventDefault() when flipping pages.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  return (
    <div className="w-full flex flex-col items-center">
      {/* Toolbar */}
      <div className="flex space-x-4 mb-2 text-sm text-gray-800">
        <button
          onClick={goToPrevPage}
          disabled={pageNumber === 1}
          className="px-3 py-1 border rounded disabled:opacity-40"
        >
          <AiOutlineDoubleLeft />
        </button>
        <span className="pt-1">
          Page {pageNumber} of {numPages}
        </span>
        <button
          onClick={goToNextPage}
          disabled={pageNumber === numPages}
          className="px-3 py-1 border rounded disabled:opacity-40"
        >
          <AiOutlineDoubleRight />
        </button>
        <button onClick={zoomOut} className="px-3 text-gray-800 py-1 border rounded">
          <AiOutlineZoomOut />
        </button>
        <button onClick={zoomIn} className="px-3 text-gray-800 py-1 border rounded">
          <AiOutlineZoomIn />
        </button>
      </div>

      {/*
        FIX 1 & 3: max-h + overflow-scroll
        ─────────────────────────────────
        • max-h-[75vh] gives the container a bounded height so the PDF
          page can actually overflow it, making scrollbars appear.
        • overflow-scroll always shows both scrollbars (vs overflow-auto
          which hides them when content fits, which breaks the wheel logic).
      */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="w-full border rounded-lg overflow-scroll"
        style={{ maxHeight: '75vh' }}
      >
        <Document file={url} onLoadSuccess={onDocumentLoadSuccess}>
          <Page
            pageNumber={pageNumber}
            width={containerWidth * scale}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Document>
      </div>
    </div>
  );
}

// ─── ContentViewer (unchanged) ──────────────────────────────────────────────

const ContentViewer = ({ url, filename }) => {
  const extension = filename.split('.').pop().toLowerCase();

  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
  const isVideo = ['mp4', 'webm', 'mov'].includes(extension);
  const isAudio = ['mp3', 'wav', 'ogg'].includes(extension);
  const isPDF = extension === 'pdf';

  return (
    <div className="w-full mx-auto my-1 p-1 bg-white shadow-lg rounded-lg">
      {isImage && (
        <div className="flex justify-center items-center w-full max-h-[75vh] overflow-auto">
          <img
            src={url}
            alt="Preview"
            className="max-h-[75vh] w-full object-contain rounded-lg"
          />
        </div>
      )}

      {isVideo && (
        <div className="flex justify-center items-center w-full max-h-[75vh] overflow-auto">
          <video
            controls
            controlsList="nodownload"
            className="max-h-[75vh] w-full object-contain rounded-lg"
          >
            <source src={url} />
            Your browser does not support the video tag.
          </video>
        </div>
      )}

      {isAudio && (
        <audio controls className="w-full mt-2">
          <source src={url} />
          Your browser does not support the audio element.
        </audio>
      )}

      {isPDF && <PDFViewer url={url} />}

      {!isImage && !isVideo && !isAudio && !isPDF && (
        <div className="text-center text-gray-500">Unsupported content type</div>
      )}
    </div>
  );
};

export default ContentViewer;
